import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import {
  today,
  formatDate,
  pct,
  calcAccuracy,
  accuracyColor,
  diffReasonOptions,
  diffInfo,
  comma,
} from "../constants";
import Card from "./ui/Card";
import Input from "./ui/Input";
import Select from "./ui/Select";
import Btn from "./ui/Btn";
import Modal from "./ui/Modal";

export default function DailyEntry({
  reviewers,
  stations,
  assignments,
  toast,
  user,
}) {
  const [form, setForm] = useState({
    date: today(),
    reviewer_id: "",
    station_id: "",
    operational_replies: "",
    send_time_status: "before",
    diff_reason: "",
    notes: "",
  });
  const [entries, setEntries] = useState([]);
  const [filterDate, setFilterDate] = useState(today());
  const [filterReviewer, setFilterReviewer] = useState("");
  const [avail, setAvail] = useState([]);
  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [otherReason, setOtherReason] = useState(false);
  const [editOtherReason, setEditOtherReason] = useState(false);
  const isAdmin = user?.role === "admin";
  const isReadOnly = user?.role === "auditor";
  const OTHER_REASON = "أخرى";

  const load = useCallback(async () => {
    const p = new URLSearchParams({
      date_from: filterDate,
      date_to: filterDate,
    });
    if (filterReviewer) p.set("reviewer_id", filterReviewer);
    try {
      const res = await api(`/entries?${p}`);
      setEntries(res?.data || res || []);
    } catch {}
  }, [filterDate, filterReviewer]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!form.reviewer_id) {
      setAvail(
        stations.map((s) => ({
          value: s.id,
          label: s.name + " — " + s.region,
        }))
      );
      return;
    }
    const a = assignments.filter(
      (x) => x.reviewer_id === parseInt(form.reviewer_id)
    );
    setAvail(
      a.length
        ? a.map((x) => ({
            value: x.station_id,
            label: x.station_name + " — " + x.region,
          }))
        : stations.map((s) => ({
            value: s.id,
            label: s.name + " — " + s.region,
          }))
    );
  }, [form.reviewer_id, assignments, stations]);

  const submit = async () => {
    if (!form.reviewer_id || !form.station_id || form.operational_replies === "") {
      toast("يرجى ملء الحقول المطلوبة", "warning");
      return;
    }
    try {
      await api("/entries", {
        method: "POST",
        body: {
          date: form.date,
          reviewer_id: parseInt(form.reviewer_id),
          station_id: parseInt(form.station_id),
          operational_replies: parseInt(form.operational_replies),
          camera_replies: 0,
          send_time_status: form.send_time_status,
          diff_reason: form.diff_reason || null,
          notes: form.notes || null,
        },
      });
      toast("✅ تم الحفظ");
      setForm((f) => ({
        ...f,
        station_id: "",
        operational_replies: "",
        diff_reason: "",
        notes: "",
      }));
      setOtherReason(false);
      load();
    } catch (e) {
      toast("خطأ: " + e.message, "error");
    }
  };

  const del = async (id) => {
    if (!window.confirm("حذف؟")) return;
    await api(`/entries/${id}`, { method: "DELETE" });
    toast("🗑️ تم");
    load();
  };

  const openEdit = (e) => {
    setEditEntry(e);
    const isOther =
      !!e.diff_reason &&
      (e.diff_reason === OTHER_REASON || !diffReasonOptions.includes(e.diff_reason));
    setEditOtherReason(isOther);
    setEditForm({
      operational_replies: String(e.operational_replies ?? 0),
      camera_replies: String(e.camera_replies ?? 0),
      send_time_status: e.send_time_status || "before",
      diff_reason: e.diff_reason === OTHER_REASON ? "" : (e.diff_reason || ""),
      notes: e.notes || "",
    });
  };

  const saveEdit = async () => {
    if (!editEntry || !editForm) return;
    if (editForm.operational_replies === "" || editForm.camera_replies === "") {
      toast("يرجى ملء الحقول المطلوبة", "warning");
      return;
    }
    try {
      await api(`/entries/${editEntry.id}`, {
        method: "PUT",
        body: {
          operational_replies: parseInt(editForm.operational_replies),
          camera_replies: parseInt(editForm.camera_replies),
          send_time_status: editForm.send_time_status,
          diff_reason: editForm.diff_reason || null,
          notes: editForm.notes || null,
        },
      });
      toast("✅ تم تعديل السجل");
      setEditEntry(null);
      setEditForm(null);
      load();
    } catch (e) {
      toast("خطأ: " + e.message, "error");
    }
  };

  return (
    <div>
      <h2
        style={{
          fontSize: 20,
          fontWeight: 800,
          margin: "0 0 20px",
          color: "#f1f5f9",
        }}
      >
        📝 الإدخال اليومي — المشرف
      </h2>
      <div style={{ display: "grid", gridTemplateColumns: isReadOnly ? "1fr" : "380px 1fr", gap: 18 }}>
        {!isReadOnly && (
        <Card>
          <h3
            style={{
              color: "#f1f5f9",
              margin: "0 0 16px",
              fontSize: 15,
              fontWeight: 700,
            }}
          >
            إدخال الردود التشغيلية
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            <Input
              label="التاريخ"
              type="date"
              value={form.date}
              onChange={(v) => setForm((f) => ({ ...f, date: v }))}
            />
            <Select
              label="المراجع"
              value={form.reviewer_id}
              onChange={(v) => setForm((f) => ({ ...f, reviewer_id: v }))}
              options={reviewers
                .filter((r) => r.active)
                .map((r) => ({ value: r.id, label: r.name }))}
              required
            />
            <Select
              label="المحطة"
              value={form.station_id}
              onChange={(v) => setForm((f) => ({ ...f, station_id: v }))}
              options={avail}
              required
            />
            <Input
              label="عدد الردود التشغيلية"
              type="number"
              value={form.operational_replies}
              onChange={(v) => setForm((f) => ({ ...f, operational_replies: v }))}
              required
            />
            <div>
              <div
                style={{
                  color: "#94a3b8",
                  fontSize: 13,
                  marginBottom: 8,
                }}
              >
                حالة الإرسال
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {[
                  ["before", "قبل ✅", "#22c55e"],
                  ["after", "بعد ⚠️", "#f59e0b"],
                  ["not_sent", "لم يُرسل ❌", "#ef4444"],
                ].map(([v, l, c]) => (
                  <button
                    key={v}
                    onClick={() =>
                      setForm((f) => ({ ...f, send_time_status: v }))
                    }
                    style={{
                      flex: 1,
                      background:
                        form.send_time_status === v ? c : "#1e293b",
                      color:
                        form.send_time_status === v ? "#fff" : "#64748b",
                      border: `1px solid ${
                        form.send_time_status === v ? c : "#334155"
                      }`,
                      borderRadius: 8,
                      padding: "8px 4px",
                      fontSize: 11,
                      fontFamily: "inherit",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <Select
              label="سبب الفرق (إن وجد)"
              value={otherReason ? OTHER_REASON : form.diff_reason}
              onChange={(v) => {
                if (v === OTHER_REASON) {
                  setOtherReason(true);
                  setForm((f) => ({ ...f, diff_reason: "" }));
                } else {
                  setOtherReason(false);
                  setForm((f) => ({ ...f, diff_reason: v }));
                }
              }}
              options={diffReasonOptions.map((x) => ({
                value: x,
                label: x,
              }))}
            />
            {otherReason && (
              <Input
                label="اكتب سبب الفرق"
                value={form.diff_reason}
                onChange={(v) => setForm((f) => ({ ...f, diff_reason: v }))}
                placeholder="اكتب السبب..."
              />
            )}
            <Input
              label="ملاحظات"
              value={form.notes}
              onChange={(v) => setForm((f) => ({ ...f, notes: v }))}
              placeholder="اختياري..."
            />
            <Btn onClick={submit} color="#22c55e" size="lg">
              💾 حفظ
            </Btn>
          </div>
        </Card>
        )}

        <Card>
          <div
            style={{
              display: "flex",
              gap: 8,
              marginBottom: 12,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              style={{
                background: "#0f172a",
                border: "1px solid #334155",
                borderRadius: 8,
                padding: "7px 10px",
                color: "#f1f5f9",
                fontSize: 12,
                fontFamily: "inherit",
              }}
            />
            <div style={{ minWidth: 150 }}>
              <Select
                value={filterReviewer}
                onChange={setFilterReviewer}
                placeholder="الكل"
                options={reviewers.map((r) => ({
                  value: r.id,
                  label: r.name,
                }))}
              />
            </div>
            <span style={{ color: "#475569", fontSize: 11 }}>
              {entries.length} سجل
            </span>
          </div>
          <div style={{ overflowX: "auto", maxHeight: 520 }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 12,
              }}
            >
              <thead>
                <tr style={{ background: "#0f172a" }}>
                  {[
                    "التاريخ",
                    "المراجع",
                    "المحطة",
                    "تشغيلي",
                    "كاميرات",
                    "الفرق",
                    "الدقة",
                    "الإرسال",
                    "السبب",
                    isReadOnly ? "" : isAdmin ? "إجراءات" : "حذف",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        padding: "8px 8px",
                        color: "#64748b",
                        textAlign: "right",
                        borderBottom: "1px solid #1e293b",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr>
                    <td
                      colSpan={10}
                      style={{
                        textAlign: "center",
                        padding: 30,
                        color: "#475569",
                      }}
                    >
                      لا توجد سجلات
                    </td>
                  </tr>
                )}
                {entries.map((e, i) => {
                  const di = diffInfo(e.diff);
                  const sc =
                    e.send_time_status === "before"
                      ? "#22c55e"
                      : e.send_time_status === "after"
                        ? "#f59e0b"
                        : "#ef4444";
                  const sl =
                    e.send_time_status === "before"
                      ? "قبل"
                      : e.send_time_status === "after"
                        ? "بعد"
                        : "لم يُرسل";
                  const acc = calcAccuracy(
                    e.camera_replies,
                    e.operational_replies
                  );
                  return (
                    <tr
                      key={e.id}
                      style={{
                        borderBottom: "1px solid #0f172a",
                        background: i % 2 ? "#ffffff03" : "transparent",
                      }}
                    >
                      <td
                        style={{
                          padding: "6px 8px",
                          color: "#94a3b8",
                          fontSize: 11,
                        }}
                      >
                        {formatDate(e.entry_date)}
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          color: "#f1f5f9",
                          fontWeight: 600,
                        }}
                      >
                        {e.reviewer_name?.split(" ")[0]}
                      </td>
                      <td style={{ padding: "6px 8px", color: "#cbd5e1" }}>
                        {e.station_name}
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          color: "#38bdf8",
                          fontWeight: 700,
                        }}
                      >
                        {e.operational_replies}
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          color: "#a78bfa",
                          fontWeight: 700,
                        }}
                      >
                        {e.camera_replies}
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          color: di.color,
                          fontWeight: 700,
                        }}
                      >
                        {comma(di.magnitude)}
                        <div style={{ fontSize: 9, fontWeight: 400, color: di.color }}>
                          {di.label}
                        </div>
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          color:
                            acc !== null
                              ? accuracyColor(acc)
                              : "#64748b",
                          fontWeight: 700,
                        }}
                      >
                        {acc !== null ? pct(acc) : "—"}
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          color: sc,
                          fontSize: 10,
                        }}
                      >
                        {sl}
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          color: "#f59e0b",
                          fontSize: 10,
                        }}
                      >
                        {e.diff_reason || "—"}
                      </td>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                        {isAdmin && !isReadOnly && (
                          <button
                            onClick={() => openEdit(e)}
                            style={{
                              background: "#0c4a6e",
                              border: "none",
                              color: "#7dd3fc",
                              borderRadius: 5,
                              padding: "2px 7px",
                              cursor: "pointer",
                              fontSize: 10,
                              marginLeft: 5,
                            }}
                          >
                            تعديل
                          </button>
                        )}
                        {!isReadOnly && (
                        <button
                          onClick={() => del(e.id)}
                          style={{
                            background: "#7f1d1d",
                            border: "none",
                            color: "#fca5a5",
                            borderRadius: 5,
                            padding: "2px 7px",
                            cursor: "pointer",
                            fontSize: 10,
                          }}
                        >
                          حذف
                        </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {editEntry && editForm && (
        <Modal
          title={`✏️ تعديل سجل — ${editEntry.station_name} — ${formatDate(editEntry.entry_date)}`}
          onClose={() => {
            setEditEntry(null);
            setEditForm(null);
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 13 }}>
            <div
              style={{
                color: "#f59e0b",
                fontSize: 11,
                background: "#78350f22",
                border: "1px solid #78350f",
                borderRadius: 6,
                padding: "6px 10px",
              }}
            >
              صلاحية المدير: تعديل الردود التشغيلية وردود الكاميرات معًا لهذا
              السجل مباشرة.
            </div>
            <Input
              label="عدد الردود التشغيلية"
              type="number"
              value={editForm.operational_replies}
              onChange={(v) =>
                setEditForm((f) => ({ ...f, operational_replies: v }))
              }
              required
            />
            <Input
              label="عدد ردود الكاميرات"
              type="number"
              value={editForm.camera_replies}
              onChange={(v) =>
                setEditForm((f) => ({ ...f, camera_replies: v }))
              }
              required
            />
            <div>
              <div style={{ color: "#94a3b8", fontSize: 13, marginBottom: 8 }}>
                حالة الإرسال
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {[
                  ["before", "قبل ✅", "#22c55e"],
                  ["after", "بعد ⚠️", "#f59e0b"],
                  ["not_sent", "لم يُرسل ❌", "#ef4444"],
                ].map(([v, l, c]) => (
                  <button
                    key={v}
                    onClick={() =>
                      setEditForm((f) => ({ ...f, send_time_status: v }))
                    }
                    style={{
                      flex: 1,
                      background: editForm.send_time_status === v ? c : "#1e293b",
                      color: editForm.send_time_status === v ? "#fff" : "#64748b",
                      border: `1px solid ${
                        editForm.send_time_status === v ? c : "#334155"
                      }`,
                      borderRadius: 8,
                      padding: "8px 4px",
                      fontSize: 11,
                      fontFamily: "inherit",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            <Select
              label="سبب الفرق (إن وجد)"
              value={editOtherReason ? OTHER_REASON : editForm.diff_reason}
              onChange={(v) => {
                if (v === OTHER_REASON) {
                  setEditOtherReason(true);
                  setEditForm((f) => ({ ...f, diff_reason: "" }));
                } else {
                  setEditOtherReason(false);
                  setEditForm((f) => ({ ...f, diff_reason: v }));
                }
              }}
              options={diffReasonOptions.map((x) => ({ value: x, label: x }))}
            />
            {editOtherReason && (
              <Input
                label="اكتب سبب الفرق"
                value={editForm.diff_reason}
                onChange={(v) => setEditForm((f) => ({ ...f, diff_reason: v }))}
                placeholder="اكتب السبب..."
              />
            )}
            <Input
              label="ملاحظات"
              value={editForm.notes}
              onChange={(v) => setEditForm((f) => ({ ...f, notes: v }))}
              placeholder="اختياري..."
            />
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <Btn onClick={saveEdit} color="#22c55e" size="lg">
                💾 حفظ التعديل
              </Btn>
              <Btn
                onClick={() => {
                  setEditEntry(null);
                  setEditForm(null);
                }}
                color="#64748b"
                size="lg"
              >
                إلغاء
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
